import type { CalendarConfig } from '../types';
import { DEFAULT_CALENDAR_CONFIG } from '../types';
import { DEFAULT_SCHEDULE_TIMEZONE, formatMinutes, parseTimeToMinutes } from '../utils/timeUtils';

function isPlainOrgMeRow(row: unknown): row is Record<string, unknown> {
  return row != null && typeof row === 'object' && !Array.isArray(row);
}

function weekdayIndicesFromOrgMe(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  const out: number[] = [];
  for (const x of raw) {
    if (typeof x === 'number' && Number.isFinite(x)) {
      const n = Math.trunc(x);
      if (n >= 0 && n <= 6) out.push(n);
    } else if (typeof x === 'string' && /^\d+$/.test(x.trim())) {
      const n = parseInt(x.trim(), 10);
      if (n >= 0 && n <= 6) out.push(n);
    }
  }
  return out;
}

/** Как `allowedWeekdaysFromOrgSettings` в Go: пустой список → пн–пт. */
function editWeekdaysFromOrgMe(raw: unknown): number[] {
  const days = weekdayIndicesFromOrgMe(raw);
  const uniq = [...new Set(days)].filter((d) => d >= 0 && d <= 6).sort((a, b) => a - b);
  if (!uniq.length) {
    return [...DEFAULT_CALENDAR_CONFIG.editWeekdays];
  }
  return uniq;
}

function stepMinutesFromOrgMe(raw: unknown): number {
  const fallback = DEFAULT_CALENDAR_CONFIG.stepMinutes;
  return stepMinutesFromRaw(raw) ?? fallback;
}

function stepMinutesFromRaw(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const t = Math.trunc(raw);
    if (t >= 1 && t <= 24 * 60) return t;
    return null;
  }
  if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) {
    const t = parseInt(raw.trim(), 10);
    if (t >= 1 && t <= 24 * 60) return t;
  }
  return null;
}

/** IANA-таймзона из снимка org/me или строки филиала; пустая/не-строка → null. */
function timezoneFromRow(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const t = raw.trim();
  return t === '' ? null : t;
}

/**
 * Эффективная таймзона расписания: филиал переопределяет организацию,
 * иначе org, иначе дефолт. Согласовано с grid-конфигом ниже.
 */
export function scheduleTimezoneFromRows(orgRow: unknown, branchRow: unknown): string {
  const branchTz = isPlainOrgMeRow(branchRow) ? timezoneFromRow(branchRow['timezone']) : null;
  if (branchTz) return branchTz;
  const orgTz = isPlainOrgMeRow(orgRow) ? timezoneFromRow(orgRow['timezone']) : null;
  return orgTz || DEFAULT_SCHEDULE_TIMEZONE;
}

const TIME_RE = /^([01]?\d|2[0-3]):([0-5]\d)$/;

function normalizeGridTime(raw: unknown, fallback: string): string {
  const s = String(raw ?? '').trim();
  const short = s.length >= 5 ? s.slice(0, 5) : s;
  if (!TIME_RE.test(short)) return fallback;
  const m = parseTimeToMinutes(short);
  if (!Number.isFinite(m)) return fallback;
  return formatMinutes(m);
}

/**
 * Снимок `GET /api/v1/organizations/me` → патч для `CalendarConfig`.
 * Согласовано с `schedule_slot_constraints.go` (дефолты сетки и дней).
 */
export function buildCalendarConfigPatchFromOrgMe(row: unknown): Partial<CalendarConfig> {
  if (!isPlainOrgMeRow(row)) {
    return {};
  }

  let editFrom = normalizeGridTime(row['schedule_edit_from'], DEFAULT_CALENDAR_CONFIG.editFrom);
  let editTo = normalizeGridTime(row['schedule_edit_to'], DEFAULT_CALENDAR_CONFIG.editTo);
  const fromM = parseTimeToMinutes(editFrom);
  const toM = parseTimeToMinutes(editTo);
  if (!(toM > fromM)) {
    editFrom = DEFAULT_CALENDAR_CONFIG.editFrom;
    editTo = DEFAULT_CALENDAR_CONFIG.editTo;
  }

  return {
    editFrom,
    editTo,
    stepMinutes: stepMinutesFromOrgMe(row['schedule_slot_step_minutes']),
    editWeekdays: editWeekdaysFromOrgMe(row['schedule_edit_weekdays']),
    timezone: scheduleTimezoneFromRows(row, null),
  };
}

/**
 * Org-level сетка + optional branch override. Филиал может переопределять шаг и таймзону;
 * окно редактирования и рабочие дни остаются дефолтами организации.
 */
export function buildCalendarConfigPatchFromOrgMeAndBranch(
  orgRow: unknown,
  branchRow: unknown
): Partial<CalendarConfig> {
  const patch = buildCalendarConfigPatchFromOrgMe(orgRow);
  if (!isPlainOrgMeRow(branchRow)) {
    return patch;
  }
  const branchStep = stepMinutesFromRaw(branchRow['schedule_slot_step_minutes']);
  return {
    ...patch,
    ...(branchStep != null ? { stepMinutes: branchStep } : {}),
    timezone: scheduleTimezoneFromRows(orgRow, branchRow),
  };
}
